import { parseEventDate, formatEventDate, formatEventTime } from '../utils/date-helpers.js';
import { renderPartyPandaMegaphone } from '../utils/mascots.js';
import { escapeHtml } from '../utils/html-utils.js';
import { readTabFromUrl, writeTabToUrl } from '../utils/tab-url.js';

const EVENT_DETAIL_DEFAULT_TAB = 'details';

// Event Detail Component
class EventDetail extends HTMLElement {
  constructor() {
    super();
    this.event = null;
    this.currentUser = null;
    this.loading = true;
    this.reminders = [];
    this.invitations = [];
    this.userInvitation = null; // Current user's invitation (for opt-out status)
    this.activeTab = 'details'; // details or invited
    this.subscriptionStatus = null; // {subscribed: bool, subscription_type: string, is_organizer: bool}
    this.showSubscriptionDropdown = false;
  }

  async connectedCallback() {
    const eventId = this.getAttribute('event-id');
    if (eventId) {
      await this.loadEvent(eventId);
    }
    this.render();
  }

  setCurrentUser(user) {
    this.currentUser = user;
    // If event is already loaded and user is organizer, load invitations
    if (this.event && this.currentUser) {
      const eventId = this.getAttribute('event-id');
      if (eventId) {
        this.loadInvitations(eventId);
        // Also load user's own invitation for recurring events (for opt-out status)
        if (this.event.is_recurring || this.event.parent_event_id) {
          this.loadUserInvitation(eventId);
        }
      }
    }
    // Also set currentUser on the comments component if it exists
    const commentsComponent = this.querySelector('#eventCommentsComponent');
    if (commentsComponent && this.currentUser) {
      commentsComponent.setCurrentUser(this.currentUser);
    }
  }

  async loadInvitations(eventId) {
    if (!this.isCurrentUserOrganizer()) {
      return;
    }

    try {
      const invitationData = await window.API.request(`/events/${eventId}/invitations`);
      this.invitations = invitationData.invitations || [];
      this.render();
    } catch (error) {
      if (error.statusCode !== 403 && error.statusCode !== 404) {
        window.logger.error('Failed to load invitations:', error);
      }
      this.invitations = [];
    }
  }

  async loadUserInvitation(eventId) {
    if (!this.currentUser) {
      return;
    }

    try {
      const invitationData = await window.API.request(`/events/${eventId}/invitations`);
      // Find current user's invitation
      this.userInvitation = invitationData.invitations?.find(
        inv => inv.user_id === this.currentUser.id
      ) || null;
      this.render();
    } catch (error) {
      // 404 or 403 is OK - user might not be invited
      if (error.statusCode !== 403 && error.statusCode !== 404) {
        window.logger.error('Failed to load user invitation:', error);
      }
      this.userInvitation = null;
    }
  }

  async loadEvent(eventId) {
    try {
      this.loading = true;
      this.event = await window.API.request(`/events/${eventId}`);

      // Load reminders if user has RSVP'd
      if (this.event.user_rsvp) {
        try {
          const reminderData = await window.API.request(`/events/${eventId}/reminders`);
          this.reminders = reminderData.reminders || [];
        } catch (error) {
          // 404 is OK - means no reminders configured yet
          if (error.statusCode !== 404) {
            window.logger.error('Failed to load reminders:', error);
          }
          this.reminders = [];
        }
      } else {
        this.reminders = [];
      }

      // Load user's invitation to get opt-out status (for recurring events)
      if (this.currentUser && (this.event.is_recurring || this.event.parent_event_id)) {
        await this.loadUserInvitation(eventId);
      } else {
        this.userInvitation = null;
      }

      // Load invitations if user is already set and is organizer
      if (this.currentUser && this.isCurrentUserOrganizer()) {
        try {
          const invitationData = await window.API.request(`/events/${eventId}/invitations`);
          this.invitations = invitationData.invitations || [];
        } catch (error) {
          if (error.statusCode !== 403 && error.statusCode !== 404) {
            window.logger.error('Failed to load invitations:', error);
          }
          this.invitations = [];
        }

        // Load subscription status
        await this.loadSubscriptionStatus(eventId);
      }

      this.activeTab = this.resolveActiveTabFromUrl();
      this.loading = false;
      this.render();
    } catch (error) {
      window.logger.error('Failed to load event:', error);
      this.loading = false;
      this.innerHTML = `
        <div class="event-container">
          <div class="error">Failed to load event. Please try again.</div>
        </div>
      `;
    }
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
      <div class="event-container">
        <div class="loading">Loading event...</div>
      </div>
    `;
      return;
    }

    if (!this.event) {
      return;
    }

    // Parse event times and convert to user's local timezone
    const eventTimezone = this.event.timezone || 'UTC';
    const startDate = parseEventDate(this.event.start_time, eventTimezone);
    const endDate = this.event.end_time ? parseEventDate(this.event.end_time, eventTimezone) : null;

    const dateStr = formatEventDate(startDate, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const timeStr = formatEventTime(startDate);
    const endTimeStr = endDate ? formatEventTime(endDate) : null;

    const isOrganizer = this.currentUser && this.event.organizers &&
      this.event.organizers.some(org => org.user_id === this.currentUser.id);

    const isInstance = !!this.event.parent_event_id;
    const isRecurringParent = this.event.is_recurring && !this.event.parent_event_id;

    // Add colored border class based on event type
    const containerClass = isInstance ? 'event-container viewing-instance' :
      isRecurringParent ? 'event-container viewing-recurring' :
        'event-container';

    const isCancelled = !!this.event.is_cancelled;

    this.innerHTML = `
      <div class="${containerClass}${isCancelled ? ' event-cancelled' : ''}">
        <div class="event-header">
          <button class="btn-back" id="backBtn">← Back to Events</button>
          <div class="event-title-row">
            <h1>${escapeHtml(this.event.title)}</h1>
            ${isCancelled ? '<span class="badge-cancelled">❌ Cancelled</span>' : ''}
            ${!isCancelled && this.event.is_public && !this.event.group_id ? '<span class="badge-public">🌍 Public Event</span>' : ''}
            ${!isCancelled && !this.event.is_public && !this.event.group_id ? '<span class="badge-private">🔒 Private Event</span>' : ''}
            ${this.event.group_id ? `<a class="badge-group" href="/groups/${this.event.group_id}">👥 Part of ${escapeHtml(this.event.group_name || 'group')}</a>` : ''}
          </div>
          ${isCancelled ? '' : this.renderOrganizerActions(isInstance)}
        </div>

        ${isCancelled ? this.renderCancelledBanner() : ''}
        ${isInstance && !isCancelled ? this.renderInstanceBanner() : ''}

        <div class="event-content">
          <div class="event-main">
            <div class="event-info-card">
              ${isOrganizer ? `
                <div class="invited-tabs">
                  <button class="tab ${this.activeTab === 'details' ? 'active' : ''}" data-tab="details">
                    📋 Event Details
                  </button>
                  <button class="tab ${this.activeTab === 'invited' ? 'active' : ''}" data-tab="invited">
                    👥 Invited People (${this.invitations.length})
                  </button>
                </div>
                <div class="tab-content-area">
                  ${this.renderTabContent(dateStr, timeStr, endTimeStr)}
                </div>
              ` : this.renderEventDetails(dateStr, timeStr, endTimeStr)}
            </div>
          </div>
        </div>

        <event-comments 
          event-id="${this.event.id}" 
          is-public="${this.event.is_public ? 'true' : 'false'}"
          id="eventCommentsComponent"
        ></event-comments>
      </div>
    `;

    this.attachEventListeners();
    this.initializeCommentsComponent();
  }

  initializeCommentsComponent() {
    const commentsComponent = this.querySelector('#eventCommentsComponent');
    if (commentsComponent) {
      if (this.currentUser) {
        commentsComponent.setCurrentUser(this.currentUser);
      }
      if (this.subscriptionStatus) {
        commentsComponent.setSubscriptionStatus(this.subscriptionStatus);
      }
      // Pass the watch button renderer
      commentsComponent.renderWatchButton = () => this.renderWatchButton();
      commentsComponent.onWatchButtonClick = () => this.toggleSubscriptionDropdown();
      commentsComponent.onWatchOptionClick = (type) => this.toggleSubscription(type);

      // Re-render comments component now that watch button is set
      commentsComponent.render();
    }
  }

  renderEventDetails(dateStr, timeStr, endTimeStr) {
    const descriptionContent = this.event.description_html ? this.event.description_html : escapeHtml(this.event.description);
    return `
      <div class="event-details">
        <div class="detail-row">
          <span class="detail-label">📅 When:</span>
          <span class="detail-value">${dateStr}, ${timeStr}${endTimeStr ? ` - ${endTimeStr}` : ''}</span>
        </div>

        ${this.renderRecurrenceInfo()}
        
        ${this.event.location ? `
          <div class="detail-row">
            <span class="detail-label">📍 Where:</span>
            <span class="detail-value">${escapeHtml(this.event.location)}</span>
          </div>
        ` : ''}

        ${descriptionContent ? `
          <div class="detail-row">
            <span class="detail-label">ℹ️ Details:</span>
            <span class="detail-value">${descriptionContent}</span>
          </div>
        ` : ''}

        ${this.event.external_links && this.event.external_links.length > 0 ? `
          <div class="detail-row">
            <span class="detail-label">🔗 Links:</span>
            <div class="detail-value">
              <ul class="links-list">
                ${this.event.external_links.map(link => `
                  <li>
                    <a class="nobtn" href="${escapeHtml(link.url)}" target="_blank" rel="noopener" class="external-link">
                      ${escapeHtml(link.label || link.url)}
                    </a>
                  </li>
                `).join('')}
              </ul>
            </div>
          </div>
        ` : ''}

        <div class="detail-row">
          <span class="detail-label">👥 Organizers:</span>
          <div class="detail-value">
            ${this.event.organizers.map(org => `
              <user-mention class="organizer-name" username="${escapeHtml(org.username)}${org.is_primary ? ' (Primary)' : ''}" real-username="${escapeHtml(org.username)}" user-id=""></user-mention>
            `).join(', ')}
          </div>
        </div>

        ${this.renderAttendeesInline()}
      </div>

      <div class="event-divider"></div>

      ${this.event.is_cancelled ? `
        <p class="cancelled-rsvp-notice">RSVP is closed for this cancelled instance.</p>
      ` : `
      <div class="rsvp-section">
        <div class="rsvp-header">
          <span class="rsvp-label">RSVP</span>
        </div>
        <div class="rsvp-options">
          <button 
            class="btn rsvp-btn ${this.event.user_rsvp === 'yes' ? 'active' : ''}" 
            data-status="yes"
          >
            Yes
          </button>
          <button 
            class="rsvp-btn ${this.event.user_rsvp === 'maybe' ? 'active' : ''}" 
            data-status="maybe"
          >
            Maybe
          </button>
          <button 
            class="btn rsvp-btn ${this.event.user_rsvp === 'no' ? 'active' : ''}" 
            data-status="no"
          >
            No
          </button>
        </div>
        ${this.event.user_rsvp ? `
          <div class="rsvp-status">
            <div>
              <p class="rsvp-current">
                ${this.event.user_rsvp === 'yes' ? 'You\'re attending!' :
    this.event.user_rsvp === 'maybe' ? 'You might attend' :
      'You\'re not attending'}
              </p>
              ${this.renderReminderInfo(this.event.user_rsvp)}
              ${this.renderRsvpReminderOptOut()}
            </div>
            <div class="reminder-button-container">
              <button class="btn-icon-round" id="configureRemindersBtn" 
                      title="Configure Reminders" aria-label="Configure Reminders">
                ${renderPartyPandaMegaphone()}
              </button>
            </div>
          </div>
        ` : ''}
      </div>
      `}
    `;
  }

  renderTabContent(dateStr, timeStr, endTimeStr) {
    if (this.activeTab === 'details') {
      return this.renderEventDetails(dateStr, timeStr, endTimeStr);
    } else if (this.activeTab === 'invited') {
      return this.renderInvitedTab();
    }
    return '';
  }

  renderInvitedTab() {
    if (this.invitations.length === 0) {
      return `
        <div class="empty-state">
          <p>No one has been invited yet.</p>
        </div>
      `;
    }

    return `
      <div class="invited-list">
        ${this.invitations.map(inv => `
          <div class="invited-item">
            <div class="invited-info">
              ${inv.username ? `
                <span class="invited-name">${escapeHtml(inv.username)}</span>
              ` : `
                <span class="invited-email">${inv.email || inv.phone || 'Unknown'}</span>
              `}
              <span class="invited-rsvp ${inv.rsvp_status}">${this.getRSVPLabel(inv.rsvp_status)}</span>
            </div>
            ${inv.user_id ? `
              <button class="btn btn-sm btn-danger" data-invitation-id="${inv.id}" data-action="revoke">Remove</button>
            ` : ''}
          </div>
        `).join('')}
      </div>
    `;
  }

  getRSVPLabel(status) {
    switch (status) {
    case 'yes': return '✅ Attending';
    case 'maybe': return '🤔 Maybe';
    case 'no': return '❌ Not attending';
    default: return '⏳ Pending';
    }
  }

  renderAttendeesInline() {
    const yesCount = this.event.yes_count || 0;
    const maybeCount = this.event.maybe_count || 0;

    if (yesCount === 0 && maybeCount === 0) {
      return '';
    }

    return `
      <div class="detail-row">
        <span class="detail-label">👥 Attendance:</span>
        <div class="detail-value">
          ${yesCount > 0 ? `<span class="attendance-stat"><strong>${yesCount}</strong> attending</span>` : ''}
          ${maybeCount > 0 ? `<span class="attendance-stat"><strong>${maybeCount}</strong> maybe</span>` : ''}
        </div>
      </div>
    `;
  }

  renderCancelledBanner() {
    const reason = this.event.cancellation_reason
      ? escapeHtml(this.event.cancellation_reason)
      : 'This occurrence has been cancelled.';
    return `
      <div class="cancelled-banner">
        <div class="cancelled-banner-content">
          <span class="cancelled-indicator">❌</span>
          <div>
            <strong>This event instance has been cancelled.</strong>
            <p class="cancelled-reason">${reason}</p>
          </div>
        </div>
      </div>
    `;
  }

  renderInstanceBanner() {
    const eventTimezone = this.event.timezone || 'UTC';
    const originalDate = this.event.original_start_time
      ? parseEventDate(this.event.original_start_time, eventTimezone)
      : null;
    const originalDateStr = originalDate ? formatEventDate(originalDate) : null;
    const originalTimeStr = originalDate ? formatEventTime(originalDate) : null;

    return `
      <div class="instance-banner">
        <div class="instance-banner-content">
          <span class="instance-indicator">📅</span>
          ${this.event.is_modified_instance && originalDateStr ? `
            <span class="modified-badge">Modified</span>
            <span class="original-date">Originally: ${originalDateStr} at ${originalTimeStr}</span>
            ${this.event.modification_reason ? `
              <span class="modification-reason">Reason: ${escapeHtml(this.event.modification_reason)}</span>
            ` : ''}
          ` : ''}
          <a href="/events/${this.event.parent_event_id}" class="link-to-parent">View Repeating Configuration →</a>
        </div>
      </div>
    `;
  }

  renderOrganizerActions(isInstance = false) {
    // Check if current user is an organizer
    if (!this.currentUser || !this.currentUser.id) {
      return '';
    }

    const isOrganizer = this.event.organizers.some(org => org.user_id === this.currentUser.id);

    if (!isOrganizer) {
      return '';
    }

    const isPrimaryOrganizer = this.event.organizers.some(org =>
      org.user_id === this.currentUser.id && org.is_primary
    );

    // For instances, show different buttons
    if (isInstance) {
      return `
        <toolbar-action-bar align="right" spacing="medium" label="Event actions">
          <toolbar-overflow-actions slot="actions">
            <button class="btn btn-primary" slot="primary" id="inviteBtn">📨 Invite People</button>
            <button class="btn btn-secondary" slot="overflow" id="editInstanceBtn">✏️ Edit Instance</button>
            <button class="btn btn-secondary" slot="overflow" id="editSeriesBtn">⚙️ Edit Series</button>
            ${isPrimaryOrganizer ? `
              <button class="btn btn-warning" slot="overflow" id="cancelInstanceBtn">❌ Cancel This Instance</button>
            ` : ''}
          </toolbar-overflow-actions>
        </toolbar-action-bar>
      `;
    }

    // For parent events, show standard buttons
    return `
      <toolbar-action-bar align="right" spacing="medium" label="Event actions">
        <toolbar-overflow-actions slot="actions">
          <button class="btn btn-primary" slot="primary" id="inviteBtn">📨 Invite People</button>
          <button class="btn btn-secondary" slot="overflow" id="editEventBtn">✏️ Edit Event</button>
          ${isPrimaryOrganizer ? `
            <button class="btn btn-danger" slot="overflow" id="deleteEventBtn">🗑️ Delete Event</button>
          ` : ''}
        </toolbar-overflow-actions>
      </toolbar-action-bar>
    `;
  }



  renderReminderInfo(status) {
    if (!this.reminders || this.reminders.length === 0) {
      // Show default message if no reminders configured
      if (status === 'yes') {
        return '<p class="reminder-info">📧 You\'ll get a reminder 1 day before</p>';
      } else if (status === 'maybe') {
        return '<p class="reminder-info">📧 You\'ll get a reminder 1 week before</p>';
      }
      return '';
    }

    // Display actual configured reminders
    const reminderList = this.reminders.map(reminder => {
      const offsetMinutes = reminder.reminder_offset_minutes;
      let timeText;

      if (offsetMinutes < 60) {
        timeText = `${offsetMinutes} minute${offsetMinutes !== 1 ? 's' : ''} before`;
      } else if (offsetMinutes < 1440) {
        const hours = Math.floor(offsetMinutes / 60);
        timeText = `${hours} hour${hours !== 1 ? 's' : ''} before`;
      } else {
        const days = Math.floor(offsetMinutes / 1440);
        timeText = `${days} day${days !== 1 ? 's' : ''} before`;
      }

      const icon = reminder.notification_type === 'email' ? '📧' : '📱';
      const type = reminder.notification_type === 'email' ? 'Email' : 'SMS';

      return `<p class="reminder-info">${icon} ${type} reminder ${timeText}</p>`;
    }).join('');

    return reminderList;
  }

  renderRsvpReminderOptOut() {
    // Only show for recurring events (parent or instance)
    if (!this.event.is_recurring && !this.event.parent_event_id) {
      return '';
    }

    // Only show if user has an invitation
    if (!this.userInvitation) {
      return '';
    }

    const isOptedOut = this.userInvitation.opt_out_rsvp_reminders || false;

    return `
      <div class="rsvp-reminder-opt-out">
        <label class="checkbox-label">
          <input 
            type="checkbox" 
            id="optOutRsvpReminders" 
            ${isOptedOut ? 'checked' : ''}
          >
          <span>Opt out of automatic RSVP reminders for this event</span>
        </label>
        <small class="help-text">
          If checked, you won't receive automatic reminders to RSVP for future instances of this recurring event.
          You can still create your own reminders.
        </small>
      </div>
    `;
  }

  renderRecurrenceInfo() {
    if (!this.event.is_recurring || !this.event.recurrence_rule) {
      return '';
    }

    const rule = this.event.recurrence_rule;
    const pattern = this.event.recurrence_pattern || rule.pattern;
    let description = '';

    switch (pattern) {
    case 'daily':
      description = `Every ${rule.interval || 1} day(s)`;
      break;
    case 'weekly':
      if (rule.days_of_week && rule.days_of_week.length > 0) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const days = rule.days_of_week.map(d => dayNames[d]).join(', ');
        const interval = rule.interval && rule.interval > 1 ? `every ${rule.interval} weeks` : 'every week';
        description = `${days} ${interval}`;
      }
      break;
    case 'monthly':
      if (rule.day_of_month) {
        description = `Day ${rule.day_of_month} of every ${rule.interval || 1} month(s)`;
      } else if (rule.monthly_relative) {
        const dayMap = { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' };
        const parts = rule.monthly_relative.split('-');
        const position = parts[0]; // first, second, third, fourth, last
        const dayName = dayMap[parts.slice(1).join('-').toLowerCase()] || parts.slice(1).join(' ');
        const interval = rule.interval && rule.interval > 1 ? `every ${rule.interval} months` : 'every month';
        description = `${position} ${dayName} ${interval}`;
      }
      break;
    default:
      description = 'Custom recurrence pattern';
    }

    if (this.event.series_end_date) {
      const endDate = parseEventDate(this.event.series_end_date, this.event.timezone || 'UTC');
      const endDateStr = formatEventDate(endDate, { year: 'numeric', month: 'long', day: 'numeric' });
      description += ` until ${endDateStr}`;
    } else {
      description += ' (continues indefinitely)';
    }

    return `
      <div class="detail-row">
        <span class="detail-label">🔄 Recurring:</span>
        <span class="detail-value">${escapeHtml(description)}</span>
      </div>
    `;
  }

  attachEventListeners() {
    // Back button
    this.querySelector('#backBtn')?.addEventListener('click', () => {
      window.router.navigate('/events');
    });

    // Reload reminders when they're saved (listen on document since modal is in body)
    document.addEventListener('reminders-saved', async (e) => {
      if (this.event && this.event.id && e.detail && e.detail.eventId === this.event.id) {
        try {
          const reminderData = await window.API.request(`/events/${this.event.id}/reminders`);
          this.reminders = reminderData.reminders || [];
          this.render();
        } catch (error) {
          if (error.statusCode !== 404) {
            window.logger.error('Failed to reload reminders:', error);
          }
        }
      }
    });

    // RSVP buttons
    this.querySelectorAll('.rsvp-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const status = e.target.dataset.status;
        await this.updateRSVP(status);
      });
    });

    // Edit button (for parent events)
    this.querySelector('#editEventBtn')?.addEventListener('click', () => {
      window.router.navigate(`/events/${this.event.id}/edit`);
    });

    // Edit Instance button (for instances)
    this.querySelector('#editInstanceBtn')?.addEventListener('click', () => {
      window.router.navigate(`/events/${this.event.id}/edit`);
    });

    // Edit Series button (for instances - goes to parent)
    this.querySelector('#editSeriesBtn')?.addEventListener('click', () => {
      window.router.navigate(`/events/${this.event.parent_event_id}/edit`);
    });

    // Cancel Instance button (for instances)
    this.querySelector('#cancelInstanceBtn')?.addEventListener('click', () => {
      this.confirmCancelInstance();
    });

    // Delete button (for parent events)
    this.querySelector('#deleteEventBtn')?.addEventListener('click', () => {
      this.confirmDeleteEvent();
    });

    // Configure reminders button
    this.querySelector('#configureRemindersBtn')?.addEventListener('click', () => {
      this.openReminderConfig();
    });

    // RSVP reminder opt-out checkbox
    const optOutCheckbox = this.querySelector('#optOutRsvpReminders');
    if (optOutCheckbox) {
      optOutCheckbox.addEventListener('change', (e) => {
        this.toggleOptOutRsvpReminders(e.target.checked);
      });
    }

    // Invite button
    this.querySelector('#inviteBtn')?.addEventListener('click', () => {
      this.openInviteModal();
    });

    // Tab switching
    this.querySelectorAll('.tab[data-tab]').forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.activeTab = e.target.dataset.tab;
        writeTabToUrl(this.activeTab, { omitDefault: EVENT_DETAIL_DEFAULT_TAB });
        this.render();
      });
    });

    // Revoke invitation buttons
    this.querySelectorAll('[data-action="revoke"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const invitationId = e.target.dataset.invitationId;
        await this.revokeInvitation(invitationId);
      });
    });

    // Watch button (comment notifications)
    this.querySelector('#watchBtn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleSubscriptionDropdown();
    });

    // Watch dropdown options
    this.querySelectorAll('.watch-option').forEach(option => {
      option.addEventListener('click', async (e) => {
        e.stopPropagation();
        const type = e.currentTarget.dataset.type;
        await this.toggleSubscription(type);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.showSubscriptionDropdown && !e.target.closest('.watch-button-container')) {
        this.showSubscriptionDropdown = false;
        this.render();
      }
    });

    // Listen for invitations sent to reload list
    document.addEventListener('event-invites-sent', async () => {
      if (this.event && this.event.id) {
        await this.loadInvitations(this.event.id);
      }
    });
  }

  async revokeInvitation(invitationId) {
    if (!confirm('Are you sure you want to remove this invitation?')) {
      return;
    }

    try {
      await window.API.request(`/events/${this.event.id}/invite/${invitationId}`, {
        method: 'DELETE'
      });

      // Reload invitations
      await this.loadInvitations(this.event.id);

      // Show success message
      const toast = document.createElement('div');
      toast.className = 'toast-notification';
      toast.textContent = 'Invitation removed';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    } catch (error) {
      alert(error.message || 'Failed to remove invitation');
    }
  }

  isCurrentUserOrganizer() {
    if (!this.currentUser || !this.event || !Array.isArray(this.event.organizers)) {
      return false;
    }
    const currentId = Number(this.currentUser.id);
    return this.event.organizers.some(org => Number(org.user_id) === currentId);
  }

  allowedEventTabs() {
    const tabs = ['details'];
    if (this.isCurrentUserOrganizer()) {
      tabs.push('invited');
    }
    return tabs;
  }

  resolveActiveTabFromUrl() {
    return readTabFromUrl(this.allowedEventTabs(), { defaultTab: EVENT_DETAIL_DEFAULT_TAB });
  }

  openInviteModal() {
    let inviteModal = document.querySelector('event-invite-modal');
    if (!inviteModal) {
      inviteModal = document.createElement('event-invite-modal');
      document.body.appendChild(inviteModal);
    }
    inviteModal.open(this.event.id);
  }

  openReminderConfig() {
    let reminderConfig = document.querySelector('reminder-config');
    if (!reminderConfig) {
      reminderConfig = document.createElement('reminder-config');
      document.body.appendChild(reminderConfig);
    }
    reminderConfig.open(this.event.id);
  }

  async toggleOptOutRsvpReminders(optOut) {
    if (!this.event || !this.userInvitation) {
      return;
    }

    try {
      await window.API.request(
        `/events/${this.event.id}/invitations/${this.userInvitation.id}/opt-out-rsvp-reminders`,
        {
          method: 'PUT',
          body: JSON.stringify({ opt_out: optOut })
        }
      );

      // Update local state
      if (this.userInvitation) {
        this.userInvitation.opt_out_rsvp_reminders = optOut;
      }

      // Show success message
      const message = optOut
        ? 'You\'ve opted out of automatic RSVP reminders'
        : 'You\'ve opted back in to automatic RSVP reminders';

      // Could show a toast notification here
      window.logger?.info(message);
    } catch (error) {
      window.logger.error('Failed to update opt-out preference:', error);
      alert('Failed to update preference. Please try again.');

      // Revert checkbox state
      const checkbox = this.querySelector('#optOutRsvpReminders');
      if (checkbox) {
        checkbox.checked = !optOut;
      }
    }
  }

  async updateRSVP(status) {
    try {
      const response = await window.API.request(`/events/${this.event.id}/rsvp`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });

      // Update local event data
      this.event.user_rsvp = response.rsvp_status;

      // Check if there's a previous instance with reminders to copy
      if (response.has_previous_instance && response.previous_instance_id && status !== 'no') {
        // Show modal to ask if user wants to copy reminders
        this.showReminderCopyPrompt(response.previous_instance_id, response.previous_instance_start_time);
        return; // Don't reload reminders yet - wait for user's choice
      }

      // Reload reminders (default reminders may have been created)
      if (this.event.user_rsvp) {
        try {
          const reminderData = await window.API.request(`/events/${this.event.id}/reminders`);
          this.reminders = reminderData.reminders || [];
        } catch (error) {
          if (error.statusCode !== 404) {
            window.logger.error('Failed to reload reminders:', error);
          }
          this.reminders = [];
        }
      } else {
        this.reminders = [];
      }

      // Reload user invitation to get opt-out status (for recurring events)
      if (this.currentUser && (this.event.is_recurring || this.event.parent_event_id)) {
        try {
          const invitationData = await window.API.request(`/events/${this.event.id}/invitations`);
          this.userInvitation = invitationData.invitations?.find(
            inv => inv.user_id === this.currentUser.id
          ) || null;
        } catch (error) {
          if (error.statusCode !== 403 && error.statusCode !== 404) {
            window.logger.error('Failed to reload user invitation:', error);
          }
          this.userInvitation = null;
        }
      }

      // Show success message
      this.showRSVPSuccess(status);

      // Re-render to update UI
      this.render();
    } catch (error) {
      window.logger.error('Failed to update RSVP:', error);
      alert('Failed to update RSVP. Please try again.');
    }
  }

  showReminderCopyPrompt(previousInstanceId, previousInstanceStartTime) {
    const previousDate = previousInstanceStartTime
      ? new Date(previousInstanceStartTime).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      : 'a previous instance';

    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'reminder-copy-modal-overlay';
    modal.innerHTML = `
      <div class="reminder-copy-modal">
        <div class="modal-header">
          <h2>📋 Copy Reminders?</h2>
        </div>
        <div class="modal-body">
          <p>
            You previously RSVPed to <strong>${escapeHtml(previousDate)}</strong>.
            Would you like to copy your reminders from that instance?
          </p>
          <div class="modal-actions">
            <button class="btn btn-primary" id="copyRemindersBtn">
              Yes, copy reminders
            </button>
            <button class="btn btn-secondary" id="setupNewRemindersBtn">
              No, set up new reminders
            </button>
            <button class="btn btn-secondary" id="useDefaultsBtn">
              Use defaults
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Event listeners
    modal.querySelector('#copyRemindersBtn').addEventListener('click', async () => {
      modal.remove();
      await this.copyRemindersFromInstance(previousInstanceId);
      this.finishRSVPUpdate();
    });

    modal.querySelector('#setupNewRemindersBtn').addEventListener('click', () => {
      modal.remove();
      this.finishRSVPUpdate();
      // Open reminder config modal
      setTimeout(() => {
        this.openReminderConfig();
      }, 100);
    });

    modal.querySelector('#useDefaultsBtn').addEventListener('click', () => {
      modal.remove();
      this.finishRSVPUpdate();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
        this.finishRSVPUpdate();
      }
    });
  }

  async copyRemindersFromInstance(sourceInstanceId) {
    try {
      await window.API.request(
        `/events/${this.event.id}/reminders/copy-from-instance/${sourceInstanceId}`,
        {
          method: 'POST'
        }
      );

      // Reload reminders
      try {
        const reminderData = await window.API.request(`/events/${this.event.id}/reminders`);
        this.reminders = reminderData.reminders || [];
      } catch (error) {
        if (error.statusCode !== 404) {
          window.logger.error('Failed to reload reminders:', error);
        }
        this.reminders = [];
      }

      // Show success message
      const toast = document.createElement('div');
      toast.className = 'toast-notification';
      toast.textContent = 'Reminders copied successfully!';
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.classList.add('show');
      }, 10);
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    } catch (error) {
      window.logger.error('Failed to copy reminders:', error);
      alert('Failed to copy reminders. You can set them up manually.');
    }
  }

  finishRSVPUpdate() {
    // Reload reminders (default reminders may have been created)
    if (this.event.user_rsvp) {
      window.API.request(`/events/${this.event.id}/reminders`)
        .then(reminderData => {
          this.reminders = reminderData.reminders || [];
        })
        .catch(error => {
          if (error.statusCode !== 404) {
            window.logger.error('Failed to reload reminders:', error);
          }
          this.reminders = [];
        })
        .finally(() => {
          this.render();
        });
    } else {
      this.reminders = [];
      this.render();
    }

    // Reload user invitation to get opt-out status (for recurring events)
    if (this.currentUser && (this.event.is_recurring || this.event.parent_event_id)) {
      window.API.request(`/events/${this.event.id}/invitations`)
        .then(invitationData => {
          this.userInvitation = invitationData.invitations?.find(
            inv => inv.user_id === this.currentUser.id
          ) || null;
          this.render();
        })
        .catch(error => {
          if (error.statusCode !== 403 && error.statusCode !== 404) {
            window.logger.error('Failed to reload user invitation:', error);
          }
          this.userInvitation = null;
          this.render();
        });
    } else {
      this.render();
    }

    // Show success message
    this.showRSVPSuccess(this.event.user_rsvp);
  }

  showRSVPSuccess(status) {
    const message = status === 'yes' ? 'Great! See you there!' :
      status === 'maybe' ? 'Got it, we\'ll remind you!' :
        'Thanks for letting us know!';

    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('show');
    }, 10);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  async confirmCancelInstance() {
    if (!this.event.parent_event_id) {
      return; // Not an instance
    }

    const confirmed = confirm(
      `Are you sure you want to cancel this instance of "${this.event.title}"?\n\n` +
      'This instance will be marked as cancelled and shown greyed out on the calendar. The rest of the series will continue.'
    );

    if (!confirmed) return;

    try {
      await window.API.request(`/events/${this.event.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          cancellation_reason: 'Instance cancelled'
        })
      });

      await this.loadEvent();
    } catch (error) {
      window.logger.error('Failed to cancel instance:', error);
      alert('Failed to cancel instance. Please try again.');
    }
  }

  async confirmDeleteEvent() {
    const confirmed = confirm(
      `Are you sure you want to delete "${this.event.title}"?\n\n` +
      'This action cannot be undone and will remove the event for all attendees.'
    );

    if (!confirmed) return;

    // Get deletion reason, required if there are attendees
    let deletionReason = 'No attendees.';
    if (this.event.yes_count > 0 || this.event.maybe_count > 0) {
      deletionReason = prompt(
        'Please provide a reason for deletion (e.g., "Event cancelled", "Venue change"):\n\n' +
        'This will help attendees understand why the event was cancelled.'
      );

      if (deletionReason === null) {
        // User cancelled
        return;
      }

      if (!deletionReason.trim()) {
        alert('Please provide a reason for deletion.');
        return;
      }
    }

    try {
      await window.API.request(`/events/${this.event.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ deletion_reason: deletionReason })
      });

      // Show success message
      const toast = document.createElement('div');
      toast.className = 'toast-notification';
      toast.textContent = 'Event deleted successfully';
      document.body.appendChild(toast);

      setTimeout(() => {
        toast.classList.add('show');
      }, 10);

      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
      }, 3000);

      // Navigate back to events list
      setTimeout(() => {
        window.router.navigate('/events');
      }, 1000);

    } catch (error) {
      window.logger.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  }

  async loadSubscriptionStatus(eventId) {
    try {
      this.subscriptionStatus = await window.API.events.getSubscriptionStatus(eventId);
    } catch (error) {
      window.logger.error('Failed to load subscription status:', error);
      this.subscriptionStatus = { subscribed: false, is_organizer: false };
    }
  }

  async toggleSubscription(type) {
    try {
      const eventId = this.event.id;

      if (type === 'off') {
        // Unsubscribe
        await window.API.events.unsubscribe(eventId);
        this.subscriptionStatus = { subscribed: false, is_organizer: this.subscriptionStatus?.is_organizer || false };
      } else {
        // Subscribe with the selected type
        const result = await window.API.events.subscribe(eventId, type);
        this.subscriptionStatus = {
          subscribed: true,
          subscription_type: result.subscription_type,
          is_organizer: this.subscriptionStatus?.is_organizer || false
        };
      }

      this.showSubscriptionDropdown = false;

      // Update the comments component with new subscription status
      const commentsComponent = this.querySelector('#eventCommentsComponent');
      if (commentsComponent) {
        commentsComponent.setSubscriptionStatus(this.subscriptionStatus);
      }
    } catch (error) {
      window.logger.error('Failed to update subscription:', error);
      alert('Failed to update notification preferences. Please try again.');
    }
  }

  toggleSubscriptionDropdown() {
    this.showSubscriptionDropdown = !this.showSubscriptionDropdown;

    // Re-render the comments component to update the dropdown
    const commentsComponent = this.querySelector('#eventCommentsComponent');
    if (commentsComponent) {
      commentsComponent.render();
    }
  }

  renderWatchButton() {
    if (!this.currentUser || !this.subscriptionStatus) {
      return '';
    }

    const subscribed = this.subscriptionStatus.subscribed;
    const type = this.subscriptionStatus.subscription_type;

    // Button state classes and text
    let buttonClass = 'watch-btn';
    const buttonText = '🔔';

    if (!subscribed) {
      buttonClass += ' watch-btn-off';
    } else if (type === 'all_comments') {
      buttonClass += ' watch-btn-all';
    } else if (type === 'my_threads') {
      buttonClass += ' watch-btn-threads';
    }

    return `
      <div class="watch-button-container">
        <button class="${buttonClass}" id="watchBtn" title="Notification preferences">
          ${buttonText} <span class="watch-btn-arrow">▼</span>
        </button>
        
        ${this.showSubscriptionDropdown ? `
          <div class="watch-dropdown">
            <button class="watch-option ${!subscribed ? 'active' : ''}" data-type="off">
              ⚪ Off (no notifications)
            </button>
            <button class="watch-option ${subscribed && type === 'all_comments' ? 'active' : ''}" data-type="all_comments">
              🔵 All Comments
            </button>
            <button class="watch-option ${subscribed && type === 'my_threads' ? 'active' : ''}" data-type="my_threads">
              🟣 My Threads Only
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('event-detail', EventDetail);

